import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import ReactConfetti from 'react-confetti';
import Modal2 from 'components/commons/modal/Modal2';
import { PremiumCheck2 as PremiumCheck } from 'components/commons/premium-check/PremiumCheck';
import Loading from 'components/commons/loading/Loading';
import { useCustomizeHeaderContext } from 'contexts/CustomizeHeaderContext';
import { useRouting } from 'hooks/useRouting';
import { useCustomHeaderTitle } from 'hooks/useCustomHeaderTitle';
import { formatNumber } from 'utils/formatter';
import { getTargetDetail } from 'config/api';
import { Target, TargetModalStatus, TargetType } from '../utils/type';
import { targetTypeToIconImg } from '../utils/targetInfo';
import { calculateTargetDate, useBirthday } from '../utils/birthday';
import { NotFoundBirthday } from '../components/features/birthday/NotFoundBirthday';
import { TargetPremium } from '../components/features/TargetPremium';

const Confetti = (): React.ReactElement => {
  return (
    <>
      <ReactConfetti
        {...{
          width: window.innerWidth,
          height: window.innerHeight,
          style: { zIndex: 9999 },
          gravity: 0.2,
          numberOfPieces: 1500,
          initialVelocityX: 13,
          initialVelocityY: 50,
          wind: 0,
          opacity: 70,
          recycle: false,
        }}
      />
    </>
  );
};

const TargetCreatedModal = ({
  onClose,
  isShow,
  target,
  birthday,
}: {
  onClose: () => void;
  target: Target;
  isShow: boolean;
  birthday: Date;
}): React.ReactElement => {
  useCustomHeaderTitle('資産目標サポート');
  const { age, month, year } = calculateTargetDate({
    birthday,
    targetDate: new Date(target.endDate),
  });

  const { setWithBell, setWithNavigation } = useCustomizeHeaderContext();
  useEffect(() => {
    return setWithBell();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    return setWithNavigation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Confetti />
      <Modal2 isShow={isShow} close={onClose}>
        <div className="p-4 max-h-modal overflow-y-auto">
          <div className="flex flex-row">
            <div
              className={`icon-close ml-auto text-gray-500 cursor-pointer`}
              onClick={onClose}
            />
          </div>
          <div
            className={`font-bold text-lg text-bluegray-700 text-center pt-1 pb-4`}>
            新しい目標が決まりました！
          </div>
          <div
            className={`bg-white rounded-lg px-4 border border-bluegray-300 pt-2 pb-4`}
            style={{ minWidth: '311px' }}>
            <div className={`grid`}>
              <img
                src={targetTypeToIconImg(target.refType)}
                alt="アイコン"
                className={`rounded-full h-10 w-10 items-start justify-center`}
                style={{
                  objectFit: 'cover',
                  border: '1px solid #dee5ee',
                  gridColumn: '1',
                  gridRow: '1',
                }}
              />
              <div
                className={`flex flex-row items-center justify-center font-bold text-lg text-bluegray-700 mx-12`}
                style={{ gridRow: '1', gridColumn: '1' }}>
                {target.title}
              </div>
            </div>
            <div
              className={`mt-2 border-b border-bluegray-300 flex flex-row items-center pb-1`}>
              <div className={`text-bluegray-700 text-sm mr-auto`}>
                目標金額
              </div>
              <div className={`font-bold text-2xl text-bluegray-700`}>
                {formatNumber(target.targetAmount / 10000, {})}
              </div>
              <div className={`font-bold text-sm ml-2 text-bluegray-700`}>
                万円
              </div>
            </div>
            <div className={`flex flex-row items-center mt-2`}>
              <div className={`text-sm text-bluegray-700 mr-auto`}>
                達成時期
              </div>
              <div className={`font-bold text-bluegray-700 text-sm ml-4`}>
                {year}年{month}月({age}歳)
              </div>
            </div>
          </div>
          <div className={`text-center text-sm text-bluegray-700 mt-5`}>
            貯め方を設定して
            <br />
            資産運用をスタートしましょう
          </div>
          <img
            src="/static/images/advisor/banzai.svg"
            alt="バンザイ"
            className={`mt-2`}
          />
          <button
            className={`mt-2 mb-3 p-2 text-center flex flex-row justify-center items-center w-full text-base`}
            onClick={onClose}>
            閉じる
          </button>
        </div>
      </Modal2>
    </>
  );
};

export const TargetRegisterInternal = ({
  target,
  birthday,
}: {
  target: Target;
  birthday: Date;
}): React.ReactElement => {
  const routing = useRouting();
  const goTargetDetail = () => {
    routing.replaceRouting({ path: `/target/${target.id}` }, true);
  };

  return (
    <>
      <TargetCreatedModal
        isShow
        {...{
          onClose: () => goTargetDetail(),
          target,
          birthday,
        }}
      />
    </>
  );
};

const TargetRegister = (): React.ReactElement => {
  const { id } = useParams<{ id: string }>();
  const { data, isFetching } = useQuery(
    'targetRegisterTargetDetail',
    () => getTargetDetailAPI(id),
    {
      refetchOnWindowFocus: false,
    }
  );

  const { birthday } = useBirthday();

  if (data === null || data === undefined || isFetching)
    return <Loading loading style={{ marginTop: '3rem' }} />;

  if (birthday === null || birthday === undefined)
    return (
      <>
        <PremiumCheck content={<TargetPremium />}>
          <NotFoundBirthday />
        </PremiumCheck>
      </>
    );

  return (
    <PremiumCheck content={<TargetPremium />}>
      <TargetRegisterInternal birthday={birthday} target={data} />
    </PremiumCheck>
  );
};

const getTargetDetailAPI = async (id: string) => {
  const res_ = await getTargetDetail(id);
  const res = {
    id: res_.ID,
    title: res_.Title,
    refType: res_.RefType as TargetType,
    startDate: res_.StartDate,
    endDate: res_.EndDate,
    targetAmount: res_.TargetAmount,
    accountList: res_.AccountList.map(list => {
      return {
        account: list.Account,
        brokerName: list.BrokerName,
        available: list.Available,
        totalAssetAmount: list.TotalAssetAmount,
      };
    }),
    excludeAmount: res_.ExcludeAmount,
    reserveAmount: res_.ReserveAmount,
    assetAmount: res_.AssetAmount,
    yield: res_.Yield,
    achieved: res_.Achieved,
    status: res_.Status as TargetModalStatus,
  };

  return res as Target;
};

export default TargetRegister;
